import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { getSupabaseClient } from '@/lib/supabaseClient';
import { needsImmediateAuth } from '@/lib/authPaths';

type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  sessionChecked: boolean;
  isAdmin: boolean;
  strippenSaldo: number;
  gebruikStrip: () => boolean;
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const immediateAuth = needsImmediateAuth(pathname);

  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(immediateAuth);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [strippenSaldo, setStrippenSaldo] = useState(3);

  const fetchAdminStatus = useCallback(async (userId?: string | null) => {
    if (!userId) {
      setIsAdmin(false);
      return;
    }

    try {
      const supabase = await getSupabaseClient();
      const { data, error } = await supabase
        .from('admins')
        .select('is_admin')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
        return;
      }

      setIsAdmin(Boolean(data?.is_admin));
    } catch (error) {
      console.warn('Supabase admin check not available:', error);
      setIsAdmin(false);
    }
  }, []);

  const resolvePendingStrippenkaarten = useCallback(async (userData?: User | null) => {
    if (!userData?.id || !userData.email) {
      return;
    }

    try {
      const supabase = await getSupabaseClient();
      const { data: pending, error } = await supabase
        .from('pending_strippenkaarten')
        .select('id, totaal_strippen')
        .eq('email', userData.email)
        .eq('status', 'pending');

      if (error) {
        console.error('Error fetching pending strippenkaarten:', error);
        return;
      }

      if (!pending || pending.length === 0) {
        return;
      }

      const successfulIds: string[] = [];
      const failedIds: string[] = [];

      for (const entry of pending) {
        const { error: insertError } = await supabase
          .from('strippenkaarten')
          .insert({
            user_id: userData.id,
            resterende_strippen: entry.totaal_strippen,
            totaal_strippen: entry.totaal_strippen,
          });

        if (insertError) {
          console.error('Error linking pending strippenkaart:', insertError);
          failedIds.push(entry.id);
          continue;
        }

        const { error: updateError } = await supabase
          .from('pending_strippenkaarten')
          .update({
            status: 'redeemed',
            redeemed_at: new Date().toISOString(),
          })
          .eq('id', entry.id);

        if (updateError) {
          console.error('Error updating pending strippenkaart status:', updateError);
          failedIds.push(entry.id);
          continue;
        }

        successfulIds.push(entry.id);
      }

      if (successfulIds.length > 0) {
        toast.success('Je openstaande strippenkaarten zijn aan je account gekoppeld.');
      }

      if (failedIds.length > 0) {
        toast.error('Een deel van de openstaande strippenkaarten kon niet gekoppeld worden. Neem contact op.');
      }
    } catch (error) {
      console.warn('Supabase pending strippenkaarten resolution not available:', error);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    let idleCallbackId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let subscription: { unsubscribe: () => void } | undefined;

    const initializeSession = async () => {
      if (immediateAuth) {
        setLoading(true);
      }

      try {
        const supabase = await getSupabaseClient();
        const {
          data: { session: activeSession },
          error,
        } = await supabase.auth.getSession();

        if (!isMounted) {
          return;
        }

        if (error) {
          console.error('Error fetching session:', error);
        }

        setSession(activeSession);
        setUser(activeSession?.user ?? null);
        await fetchAdminStatus(activeSession?.user?.id);
        await resolvePendingStrippenkaarten(activeSession?.user ?? null);

        const {
          data: { subscription: authSubscription },
        } = supabase.auth.onAuthStateChange((_event, nextSession) => {
          setSession(nextSession);
          setUser(nextSession?.user ?? null);
          fetchAdminStatus(nextSession?.user?.id);
          resolvePendingStrippenkaarten(nextSession?.user ?? null);
          setLoading(false);
        });

        subscription = authSubscription;
      } catch (error) {
        console.warn('Supabase not available, running in development mode:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
          setSessionChecked(true);
        }
      }
    };

    const startInit = () => {
      void initializeSession();
    };

    if (immediateAuth) {
      startInit();
    } else if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleCallbackId = window.requestIdleCallback(startInit, { timeout: 3000 });
    } else {
      timeoutId = setTimeout(startInit, 1500);
    }

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
      if (idleCallbackId !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackId);
      }
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [fetchAdminStatus, immediateAuth, resolvePendingStrippenkaarten]);

  const signUp = async (email: string, password: string) => {
    try {
      const supabase = await getSupabaseClient();
      const { error } = await supabase.auth.signUp({
        email,
        password,
      });
      return { error };
    } catch (error) {
      console.warn('Supabase signUp not available:', error);
      return { error: error as AuthError };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const supabase = await getSupabaseClient();
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { error };
    } catch (error) {
      console.warn('Supabase signIn not available:', error);
      return { error: error as AuthError };
    }
  };

  const signOut = async () => {
    try {
      const supabase = await getSupabaseClient();
      await supabase.auth.signOut();
    } catch (error) {
      console.warn('Supabase signOut not available:', error);
    }
    setStrippenSaldo(3);
  };

  const gebruikStrip = (): boolean => {
    if (strippenSaldo > 0) {
      setStrippenSaldo(strippenSaldo - 1);
      return true;
    }
    return false;
  };

  const value = {
    user,
    session,
    loading,
    sessionChecked,
    isAdmin,
    strippenSaldo,
    gebruikStrip,
    signUp,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
